import { formatInteger } from '../../format';

/**
 * Anterior, Próxima, and where the reader is.
 *
 * Deliberately ignorant of where the rows come from: it takes a page, a page
 * size and a total and reports the page somebody asked for. A list paged by the
 * API keeps its own page in state and passes it here; a list paged in the
 * browser gets all four from `usePagination`. Either way the control looks and
 * reads the same.
 *
 * Shaped for reading, not for jumping: two buttons and two sentences rather
 * than a row of numbered links. The lists it serves are a few pages long, and
 * "Página 2 de 4" with "11–20 de 37" says both how far through and how much is
 * left, which a strip of numbers only implies. The buttons are disabled at the
 * ends rather than hidden, so they never move under the pointer between one
 * click and the next; and when everything fits on one page the control is not
 * rendered at all, because "Página 1 de 1" is a sentence about nothing.
 *
 * The page sentence is a polite live region, so a screen reader hears where a
 * press of Próxima landed without having to go looking.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) {
    return null;
  }
  const current = Math.min(Math.max(1, page), pageCount);
  const first = (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, total);

  return (
    <nav className="pagination" aria-label="Paginação">
      <button
        type="button"
        className="button button--secondary"
        disabled={current <= 1}
        onClick={() => onPageChange(current - 1)}
      >
        Anterior
      </button>
      <div className="pagination__status">
        <p className="pagination__page" aria-live="polite">
          Página {formatInteger(current)} de {formatInteger(pageCount)}
        </p>
        <p className="pagination__range">
          {formatInteger(first)}–{formatInteger(last)} de {formatInteger(total)}
        </p>
      </div>
      <button
        type="button"
        className="button button--secondary"
        disabled={current >= pageCount}
        onClick={() => onPageChange(current + 1)}
      >
        Próxima
      </button>
    </nav>
  );
}
